/* eslint-disable jsx-a11y/anchor-is-valid */
import { Modal, Spinner } from "flowbite-react";
import { Button } from "@components";
import { useEffect, useRef, type FC, type MouseEvent } from "react";
import { cn, modalFooter } from "@design-system";
import { modalPortalRoot } from "../../helpers/modalRoot";

interface DialogProp {
  show: boolean;
  placeButtonsLeft?: boolean;
  buttonConfirmText?: string;
  confirmButtonColor?: string;
  buttonConfirmPrependIcon?: React.ReactNode;
  buttonCloseText?: string;
  buttonClosePrependIcon?: React.ReactNode;
  isLoading?: boolean;
  hideButtons?: boolean;
  hideTitle?: boolean;
  denseHeader?: boolean;
  hideHeader?: boolean;
  addDisabled?: boolean;
  title?: string;
  content?: React.ReactNode;
  dismissible?: boolean;
  /**
   * sm,md,lg,xl,2xl,
   * 3xl,4xl,5xl,6xl,7xl
   *
   * Defaults to 3xl
   */
  size?: string;
  toggleModal: () => void;
  confirmButton?: () => void;
}
/**
 * TODO:
 * Fullscreen on mobile
 */

export const Dialog: FC<DialogProp> = (props) => {
  useEffect(() => {
    if (!props.show) {
      //Remove 'overflow: hidden' which is being set to body by Flowbite.
      document.getElementsByTagName("body")[0].style.overflow = "";
    }
  }, [props.show]);

  // Restore body scroll if the dialog is unmounted while still open. Some call
  // sites conditionally render the dialog ({show && <Dialog/>}), so it can be
  // removed before the `show === false` effect above ever runs, which would
  // otherwise leave Flowbite's `overflow: hidden` stuck on <body>.
  useEffect(() => {
    return () => {
      document.getElementsByTagName("body")[0].style.overflow = "";
    };
  }, []);

  const close = () => {
    props.toggleModal();
  };

  // Tracks whether the current pointer interaction *started* on the backdrop.
  // Flowbite dismisses on any backdrop click, but a text selection that begins
  // inside the panel and ends outside it fires a click whose target is the
  // backdrop, wrongly closing the modal. We only dismiss when the press both
  // starts and ends on the backdrop itself.
  const pointerDownOnBackdropRef = useRef(false);

  const handleBackdropMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    pointerDownOnBackdropRef.current = e.target === e.currentTarget;
  };

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    const startedAndEndedOnBackdrop =
      e.target === e.currentTarget && pointerDownOnBackdropRef.current;
    pointerDownOnBackdropRef.current = false;
    if (props.dismissible && startedAndEndedOnBackdrop) {
      close();
    }
  };

  // Create custom class for extra wide modal. `lgb-dialog` is always applied so
  // the injected min-width rule below gives every dialog a sensible, responsive
  // minimum size instead of collapsing to the content width.
  const getModalClasses = () => {
    const baseClass = "animate-in fade-in duration-300 lgb-dialog";
    // For 7xl size, we add a custom class to make it very wide
    if (props.size === "7xl") {
      return `${baseClass} ultra-wide-modal`;
    }
    return baseClass;
  };

  // Use useEffect to add the custom CSS for the ultra-wide-modal class
  useEffect(() => {
    // Create style element for the ultra-wide-modal
    const styleElement = document.createElement("style");
    styleElement.id = "ultra-wide-modal-style";
    styleElement.innerHTML = `
      /* Give every dialog a responsive minimum width so it never looks tiny,
         while still shrinking to fit small screens. */
      .lgb-dialog > div {
        min-width: min(90vw, 28rem);
      }
      /* Custom style for ultra-wide-modal */
      .ultra-wide-modal > div {
        width: 70vw !important;
        max-width: 1000px !important;
      }
    `;

    // Add the style element to the document head
    document.head.appendChild(styleElement);

    // Clean up the style element when the component is unmounted
    return () => {
      const element = document.getElementById("ultra-wide-modal-style");
      if (element) {
        document.head.removeChild(element);
      }
    };
  }, []);

  return (
    <div className="relative">
      {props.show && (
        <Modal
          root={modalPortalRoot}
          show={props.show}
          size={props.size || "3xl"}
          className={cn(getModalClasses())}
          onClose={close}
          onMouseDown={handleBackdropMouseDown}
          onClick={handleBackdropClick}
        >
          <Modal.Header
            className={cn(
              "dark:border-border dark:bg-surface-modal",
              props.denseHeader ? "" : " !p-6 !pb-2",
            )}
          >
            {!props.hideTitle && props.title}
          </Modal.Header>
          <Modal.Body className="dark:bg-surface-modal">
            <div>{props.content}</div>
          </Modal.Body>
          {!props.hideButtons && (
            <Modal.Footer
              className={cn(
                modalFooter,
                "border-0 pt-0 gap-4 dark:bg-surface-modal",
                props.placeButtonsLeft ? "justify-start" : "justify-end",
              )}
            >
              <div
                className={
                  "flex gap-4" +
                  (props.placeButtonsLeft ? "" : " flex-row-reverse")
                }
              >
                {!props.isLoading && (
                  <Button
                    disabled={props.addDisabled}
                    color={props.confirmButtonColor || "primary"}
                    onClick={() =>
                      props.confirmButton ? props.confirmButton() : null
                    }
                  >
                    {props.buttonConfirmPrependIcon}
                    {props.buttonConfirmText ?? "Accept"}
                  </Button>
                )}
                {props.isLoading && (
                  <Button color={props.confirmButtonColor || "primary"}>
                    <Spinner size={"sm"} />
                  </Button>
                )}
                <Button color="gray" onClick={close}>
                  {props.buttonClosePrependIcon}
                  {props.buttonCloseText ?? "Close"}
                </Button>
              </div>
            </Modal.Footer>
          )}
        </Modal>
      )}
    </div>
  );
};
